<template>
  <transition name="modal">
    <div class="modal fade show d-block" tabindex="-1" role="dialog" aria-modal="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <!-- Header -->
          <div class="modal-header">
            <h1 class="modal-title fs-5">{{ $t("signup.title") }}</h1>
            <button type="button" class="btn-close" aria-label="Close" @click="closeModal"></button>
          </div>

          <!-- Body -->
          <div class="modal-body">
            <!-- 회원가입 폼 -->
            <template v-if="signupStep === 'form'">
              <form @submit.prevent="submitForm" novalidate>
                <!-- 이름 -->
                <div class="mb-3">
                  <input type="text" id="userName" v-model="userName" class="form-control" required placeholder="이름"
                    autofocus @blur="filterUserName" />
                </div>

                <!-- 전화번호 -->
                <div class="mb-3">
                  <input type="tel" id="phoneNumber" v-model="phoneNumber" class="form-control" required
                    placeholder="전화번호 (예: 010-1234-5678)" maxlength="13"
                    @input="formatPhoneNumber" />
                </div>

                <!-- 회사명 -->
                <div class="mb-3">
                  <input type="text" id="company" v-model="company" class="form-control" placeholder="회사명 (선택)" />
                </div>

                <!-- 이메일 -->
                <div class="mb-3">
                  <input type="email" id="email" v-model="email" class="form-control" required
                    :placeholder="$t('accountCommon.emailPlaceholder')"
                    @input="email = email.toLowerCase()" />
                </div>

                <!-- 비밀번호 -->
                <div class="mb-2">
                  <div class="input-group">
                    <input :type="isPasswordVisible ? 'text' : 'password'" id="password" v-model="password"
                      @input="passwordTouched = true" required :placeholder="$t('accountCommon.passwordPlaceholder')"
                      class="form-control" :class="{
                        'is-invalid':
                          (passwordTouched || repeatTouched) &&
                          (passwordValidationError || passwordMismatchError),
                      }" />
                    <button type="button" class="btn btn-outline-secondary" @click="togglePasswordVisibility">
                      {{
                        isPasswordVisible
                          ? $t("accountCommon.hidePassword")
                          : $t("accountCommon.showPassword")
                      }}
                    </button>
                  </div>

                  <!-- 비밀번호 규칙 / 에러 -->
                  <div v-if="passwordTouched && passwordValidationError && password" class="invalid-feedback d-block">
                    {{ $t("accountCommon.errors.passwordRule") }}
                  </div>

                  <div v-if="
                    repeatTouched &&
                    passwordMismatchError &&
                    (password || repeatPassword)
                  " class="invalid-feedback d-block">
                    {{ $t("accountCommon.errors.passwordMismatch") }}
                  </div>

                  <ul class="list-unstyled small mt-2 mb-0">
                    <li :class="password.length >= 8 ? 'text-success' : 'text-secondary'">
                      &#128900;
                      {{ $t("accountCommon.passwordRules.minLength") }}
                    </li>
                    <li :class="hasNumber ? 'text-success' : 'text-secondary'">
                      &#128900; {{ $t("accountCommon.passwordRules.number") }}
                    </li>
                  </ul>
                </div>

                <!-- 비밀번호 확인 -->
                <div class="mb-3">
                  <div class="input-group">
                    <input :type="isRepeatPasswordVisible ? 'text' : 'password'" id="repeatPassword"
                      v-model="repeatPassword" @input="repeatTouched = true" required
                      :placeholder="$t('accountCommon.repeatPasswordPlaceholder')" class="form-control" :class="{
                        'is-invalid': repeatTouched && passwordMismatchError,
                      }" />
                    <button type="button" class="btn btn-outline-secondary" @click="toggleRepeatPasswordVisibility">
                      {{
                        isRepeatPasswordVisible
                          ? $t("accountCommon.hidePassword")
                          : $t("accountCommon.showPassword")
                      }}
                    </button>
                  </div>
                </div>

                <!-- 가입 사유 -->
                <div class="mb-3">
                  <textarea id="requestContent" v-model="requestContent" class="form-control" rows="2"
                    placeholder="가입 사유 (선택)"></textarea>
                </div>

                <!-- 필수 확인 -->
                <div class="form-check mb-2">
                  <input class="form-check-input" type="checkbox" id="agreeTerms" v-model="agreeTerms" required />
                  <label class="form-check-label" for="agreeTerms">
                    <span>{{ $t("signup.terms.agreePrefix") }}</span>
                    <button type="button" class="btn btn-link p-0 align-baseline" @click="openTerms">
                      {{ $t("signup.terms.terms") }}
                    </button>
                    <span>{{ $t("signup.terms.and") }}</span>
                    <button type="button" class="btn btn-link p-0 align-baseline" @click="openPrivacy">
                      {{ $t("signup.terms.privacy") }}
                    </button>
                    <span>{{ $t("signup.terms.agreeSuffix") }}</span>
                  </label>
                </div>

                <!-- 메일구독 -->
                <div class="form-check mb-3">
                  <input class="form-check-input" type="checkbox" id="emailOptIn" v-model="emailOptIn" />
                  <label class="form-check-label" for="emailOptIn">
                    {{ $t("signup.emailOptIn") }}
                  </label>
                </div>

                <!-- 제출 -->
                <div class="d-grid">
                  <button type="submit" class="btn btn-primary">
                    {{ $t("accountCommon.submitBtn") }}
                  </button>
                </div>

                <!-- 안내/에러 -->
                <div v-if="isResendSuccess" class="alert alert-success mt-3 mb-0" role="alert">
                  {{ $t("signup.resendSuccess") }}
                </div>
                <div v-if="errorMessage" class="alert alert-danger mt-3 mb-0" role="alert">
                  {{ errorMessage }}
                </div>
              </form>
            </template>

            <!-- 인증 코드 입력 -->
            <template v-else-if="signupStep === 'verify'">
              <div class="mb-2">{{ $t("accountCommon.verificationCode") }}</div>

              <div class="mb-3">
                <input type="text" v-model="authSignupCode" class="form-control" required
                  :placeholder="$t('accountCommon.codePlaceholder')" :class="{ 'is-invalid': codeMismatchError }" />
                <div v-if="codeMismatchError" class="invalid-feedback d-block">
                  {{ $t("accountCommon.errors.codeMismatch") }}
                </div>
              </div>

              <p class="small text-secondary">
                {{ $t("accountCommon.noEmail") }}
                <button type="button" class="btn btn-link p-0 align-baseline" @click="resendCode"
                  :disabled="isButtonDisabled">
                  {{ $t("accountCommon.resend") }}
                </button>
              </p>

              <div class="d-grid">
                <button @click="completeSignup" class="btn btn-primary" type="button">
                  {{ $t("accountCommon.submitBtn") }}
                </button>
              </div>

              <div v-if="errorMessage" class="alert alert-danger mt-3 mb-0" role="alert">
                {{ errorMessage }}
              </div>
            </template>

            <!-- 회원가입 완료 -->
            <template v-else-if="signupStep === 'success'">
              <div class="text-center py-4">
                <div class="alert alert-success" role="alert">
                  {{ $t("signup.success.message") }}
                </div>
                <button @click="closeModal" class="btn btn-primary" type="button">
                  {{ $t("accountCommon.loginBtn") }}
                </button>
              </div>
            </template>
          </div>

          <!-- Footer -->
          <div class="modal-footer flex-column align-items-stretch">
            <!-- 재인증하기 (form 단계에서만) -->
            <div v-if="signupStep === 'form' && needsReauthentication" class="alert alert-warning w-100 mb-2"
              role="alert">
              <div class="d-flex justify-content-between align-items-center">
                <span>{{ $t("signup.reauth.prompt") }}</span>
                <button type="button" class="btn btn-link p-0" @click="handleReauthentication">
                  {{ $t("signup.reauth.button") }}
                </button>
              </div>
            </div>

            <!-- 로그인 링크 (success 제외) -->
            <div v-if="signupStep !== 'success'" class="d-flex w-100 justify-content-between align-items-center">
              <p class="mb-0">{{ $t("signup.loginPrompt") }}</p>
              <button type="button" class="btn btn-outline-primary" @click="switchToLogin">
                {{ $t("signup.loginLink") }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>

  <!-- Backdrop -->
  <div class="modal-backdrop fade show"></div>
</template>


<script setup>
import { ref, computed, defineEmits, onMounted, watch } from "vue";
import userApi from "@/common/api/userApi";
import { logDebug, logError } from "@/utils/logHelper";
import { useI18n } from "vue-i18n";

const emit = defineEmits(["close", "signup-closed"]);
const { t } = useI18n();

const props = defineProps({
  prefillEmail: { type: String, default: "" },
  startStep: { type: String, default: "form" }, // 'form' | 'verify' | 'success'
});

// 상태
const email = ref("");
const password = ref("");
const repeatPassword = ref("");
const userName = ref("");
const phoneNumber = ref("");
const company = ref("");
const requestContent = ref("");
const agreeTerms = ref(false);
const emailOptIn = ref(false);

const passwordValidationError = ref(false);
const passwordMismatchError = ref(false);

const isPasswordVisible = ref(false);
const isRepeatPasswordVisible = ref(false);

const passwordTouched = ref(false);
const repeatTouched = ref(false);

// 인증 관련
const signupStep = ref("form");
const authSignupCode = ref("");
const codeMismatchError = ref(false);
const errorMessage = ref("");
const isResendSuccess = ref(false);
const isButtonDisabled = ref(false);
const needsReauthentication = ref(false);

// 규칙 체크
const hasNumber = computed(() => /\d/.test(password.value));

const isPasswordValid = computed(() => {
  if (!password.value) return true;
  return password.value.length >= 8 && hasNumber.value;
});

const isPasswordSame = computed(() => {
  // 확인칸이 비어있으면 mismatch 안 띄움
  if (!repeatPassword.value) return true;
  return password.value === repeatPassword.value;
});

// 초기 진입
onMounted(() => {
  if (props.prefillEmail) email.value = props.prefillEmail;
  if (props.startStep === "verify") {
    signupStep.value = "verify";
    reqSignupCode();
  }
});

watch(
  () => props.prefillEmail,
  (nv) => {
    if (nv) email.value = nv;
  }
);

watch(
  () => props.startStep,
  (nv) => {
    if (nv === "verify") {
      signupStep.value = "verify";
      reqSignupCode();
    }
  }
);

// ✅ 입력 중 실시간 검증 (핵심)
watch([password, repeatPassword, passwordTouched, repeatTouched], () => {
  // 조건 불일치: 비번 입력 시작 후 즉시 반영
  passwordValidationError.value = passwordTouched.value && !isPasswordValid.value;

  // 불일치: 확인칸 입력 시작 후 즉시 반영
  passwordMismatchError.value = repeatTouched.value && !isPasswordSame.value;

  // 둘 다 비면 에러/터치 상태 초기화
  if (!password.value && !repeatPassword.value) {
    passwordValidationError.value = false;
    passwordMismatchError.value = false;
    passwordTouched.value = false;
    repeatTouched.value = false;
  }
});

function filterUserName() {
  userName.value = userName.value.replace(/[^가-힣a-zA-Z\s]/g, "");
}

function formatPhoneNumber(e) {
  const digits = e.target.value.replace(/\D/g, "").slice(0, 11);
  let formatted = digits;
  if (digits.length > 7) {
    formatted = `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
  } else if (digits.length > 3) {
    formatted = `${digits.slice(0, 3)}-${digits.slice(3)}`;
  }
  phoneNumber.value = formatted;
}

function togglePasswordVisibility() {
  isPasswordVisible.value = !isPasswordVisible.value;
}

function toggleRepeatPasswordVisibility() {
  isRepeatPasswordVisible.value = !isRepeatPasswordVisible.value;
}

function submitForm() {
  errorMessage.value = "";
  needsReauthentication.value = false;

  // 제출 시에는 강제로 에러 노출
  passwordTouched.value = true;
  repeatTouched.value = true;

  passwordValidationError.value = !isPasswordValid.value;
  passwordMismatchError.value = !isPasswordSame.value;

  if (passwordValidationError.value || passwordMismatchError.value) return;

  if (!userName.value.trim()) {
    errorMessage.value = "이름을 입력해 주세요.";
    return;
  }
  if (!phoneNumber.value.trim()) {
    errorMessage.value = "전화번호를 입력해 주세요.";
    return;
  }
  if (phoneNumber.value.replace(/\D/g, "").length !== 11) {
    errorMessage.value = "전화번호 11자리를 입력해 주세요.";
    return;
  }
  if (!agreeTerms.value) {
    errorMessage.value = "이용약관 및 개인정보 보호정책에 동의해 주세요.";
    return;
  }

  signup({
    userEmail: email.value,
    password: password.value,
    passwordConfirm: repeatPassword.value,
    userName: userName.value.trim(),
    phoneNumber: phoneNumber.value.trim(),
    company: company.value.trim() || null,
    requestContent: requestContent.value.trim() || null,
    emailOptIn: emailOptIn.value ? 1 : 0,
  });
}

async function signup(signupData) {
  try {
    await userApi.signup(signupData);
    signupStep.value = "verify";
    await reqSignupCode();
  } catch (error) {
    console.log("signup error raw:", error);
    console.log("axios response data:", error?.response?.data);
    console.log("custom errorResponse:", error?.errorResponse);
    const serverError = error.errorResponse || {};
    errorMessage.value =
      serverError.message || error.message || "회원가입 요청 중 문제가 발생했습니다.";
    logError("회원가입 오류:", error);

    if (serverError.errorCode === "E505") {
      needsReauthentication.value = true;
    }
  }
}

async function reqSignupCode() {
  try {
    await userApi.reqcode({ userEmail: email.value });
    logDebug("인증 코드 요청 성공");
  } catch (error) {
    errorMessage.value = error.errorResponse?.message || "인증 코드 요청 실패";
    logError("인증 코드 요청 오류:", error);
  }
}

async function completeSignup() {
  try {
    await userApi.verify({
      userEmail: email.value,
      authCode: authSignupCode.value,
    });
    signupStep.value = "success";
  } catch (error) {
    codeMismatchError.value = true;
    errorMessage.value =
      error.errorResponse?.message || "인증 확인 중 문제가 발생했습니다.";
    logError("인증 확인 오류:", error);
  }
}

async function resendCode() {
  alert("인증 코드가 재전송되었습니다.");
  isButtonDisabled.value = true;

  try {
    await userApi.reqcode({ userEmail: email.value });
    isResendSuccess.value = true;
    logDebug("인증 코드 재발송 성공");
  } catch (error) {
    errorMessage.value = error.errorResponse?.message || "인증 코드 재발송 실패";
    logError("인증 코드 재발송 오류:", error);
  } finally {
    setTimeout(() => {
      isButtonDisabled.value = false;
    }, 5000);
  }
}

function closeModal() {
  emit("close");
  emit("signup-closed", { signupStep: signupStep.value });
}

function switchToLogin() {
  emit("close");
  emit("signup-closed", { signupStep: signupStep.value });
}

function openTerms() {
  const termsUrl = `${window.location.origin}/useTerm`;
  window.open(termsUrl, "_blank");
}

function openPrivacy() {
  const privacyUrl = `${window.location.origin}/privacyTerm`;
  window.open(privacyUrl, "_blank");
}

async function handleReauthentication() {
  if (!email.value) {
    errorMessage.value = "이메일을 입력해 주세요.";
    return;
  }
  signupStep.value = "verify";
  await reqSignupCode();
}
</script>
